import { useMemo } from 'react';
import { Avatar, Flex, Typography, theme } from 'antd';
import { Line } from 'recharts';
import TimeSeriesLineChart from '@/components/charts/TimeSeriesLineChart';
import type { ChartGranularity } from '@/types';
import type { PlayerDownloadsMetricsResponse } from '@/types/api/shows';
import { checkboxColors, createGranularityXAxisFormatter, formatNumber } from '@/utils';
import { formatBigNumbers } from '@/utils/numberFormattingUtils';
import { shortMonthNames } from '@/utils/dateUtils';

interface TopPlayersModalChartRow {
  id: string;
  label: string;
  metricsKey: string;
}

interface ChartDataPoint {
  date: string;
  [topPlayerId: string]: number | string;
}

interface TopPlayersModalChartEchoProps {
  selectedRows: TopPlayersModalChartRow[];
  dailyMetrics: PlayerDownloadsMetricsResponse | undefined;
  getColor: (id: string) => number | undefined;
  granularity: ChartGranularity;
}

const CHART_HEIGHT = 300;
const LABEL_MAX_WIDTH = 140;
const SWATCH_SIZE = 12;

const buildChartData = (
  selectedRows: TopPlayersModalChartRow[],
  dailyMetrics: PlayerDownloadsMetricsResponse | undefined
): ChartDataPoint[] => {
  const dateMap = new Map<string, ChartDataPoint>();

  selectedRows.forEach((row) => {
    const metrics = dailyMetrics?.[row.metricsKey] ?? [];
    metrics.forEach(({ date, downloadsAndViews }) => {
      const point = dateMap.get(date) ?? { date };
      point[row.id] = downloadsAndViews;
      dateMap.set(date, point);
    });
  });

  return Array.from(dateMap.values()).sort((first, second) => first.date.localeCompare(second.date));
};

const TopPlayersChartTooltipContent = ({
  payload,
  selectedRows,
  getColor,
  granularity,
}: {
  payload: ChartDataPoint;
  selectedRows: TopPlayersModalChartRow[];
  getColor: (id: string) => number | undefined;
  granularity: ChartGranularity;
}) => {
  const { token } = theme.useToken();

  if (!payload?.date) return null;

  const [year, month, day] = (payload.date as string).split('-');
  const monthName = shortMonthNames[parseInt(month, 10) - 1];
  const formattedDate =
    granularity === 'month'
      ? `${monthName} ${year}`
      : granularity === 'week'
        ? `Week of ${monthName} ${parseInt(day, 10)}`
        : `${monthName} ${day}`;

  const inverseTextStyle = { color: token.colorTextLightSolid };

  return (
    <Flex vertical gap={token.marginXXS}>
      <Typography.Text strong style={{ ...inverseTextStyle, marginInlineEnd: token.marginLG }}>
        {formattedDate}
      </Typography.Text>
      {selectedRows.map((row) => {
        const value = payload[row.id];
        if (value == null) return null;
        const colorIndex = getColor(row.id);
        const color = colorIndex != null ? checkboxColors[colorIndex] : undefined;

        return (
          <Flex key={row.id} align="center" gap={token.marginXXS}>
            <Avatar size={SWATCH_SIZE} style={{ backgroundColor: color }} />
            <Typography.Text ellipsis style={{ ...inverseTextStyle, maxWidth: LABEL_MAX_WIDTH }}>
              {row.label}:
            </Typography.Text>
            <Typography.Text strong style={inverseTextStyle}>
              {formatNumber('en', value as number)}
            </Typography.Text>
          </Flex>
        );
      })}
    </Flex>
  );
};

const TopPlayersModalChartEcho = ({
  selectedRows,
  dailyMetrics,
  getColor,
  granularity,
}: TopPlayersModalChartEchoProps) => {
  const { token } = theme.useToken();
  const chartData = useMemo(
    () => buildChartData(selectedRows, dailyMetrics),
    [dailyMetrics, selectedRows]
  );

  if (selectedRows.length < 1) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{ height: CHART_HEIGHT }}
      >
        <Typography.Text style={{ color: token.colorTextSecondary, fontSize: token.fontSizeSM }}>
          Select apps or websites in the table to show them in the graph.
        </Typography.Text>
      </Flex>
    );
  }

  return (
    <TimeSeriesLineChart<ChartDataPoint>
      data={chartData}
      dateKey="date"
      xAxisTickFormatter={createGranularityXAxisFormatter(granularity)}
      yAxisFormatter={(value) => (value !== 0 ? formatBigNumbers(value) : '')}
      ariaLabel="Top apps and websites performance over time"
      tooltipContent={(payload) => (
        <TopPlayersChartTooltipContent
          payload={payload}
          selectedRows={selectedRows}
          getColor={getColor}
          granularity={granularity}
        />
      )}
      height={CHART_HEIGHT}
    >
      {selectedRows.map((row) => (
        <Line
          key={`line-${row.id}`}
          name={row.label}
          dataKey={row.id}
          stroke={checkboxColors[getColor(row.id) ?? 0]}
          dot={false}
          connectNulls
          isAnimationActive={true}
          type="linear"
          strokeWidth={2}
        />
      ))}
    </TimeSeriesLineChart>
  );
};

export default TopPlayersModalChartEcho;
